import axios from 'axios';
import { useEffect, useState } from 'react';
import type { StatuspageStatus } from '~/components/HealthStatus/types';
import type { StatuspageComponentsResponse, UseStatuspageResult } from './types';

function useStatuspage(): UseStatuspageResult {
  const [overallStatus, setOverallStatus] = useState<StatuspageStatus>('operational');
  const [networkStatus, setNetworkStatus] = useState<StatuspageStatus | undefined>('operational');
  const [webhooksStatus, setWebhooksStatus] = useState<StatuspageStatus | undefined>('operational');
  const [apiStatus, setApiStatus] = useState<StatuspageStatus | undefined>('operational');
  const [appStatus, setAppStatus] = useState<StatuspageStatus | undefined>('operational');
  const isDevelopment = process.env.NODE_ENV === 'development';

  useEffect(() => {
    if (isDevelopment) return;
    let cancelFetcher: ReturnType<typeof setTimeout>;

    startFetcher(4000);

    return () => clearTimeout(cancelFetcher);

    async function startFetcher(waitTime: number) {
      cancelFetcher = setTimeout(async () => {
        const {
          data: { components },
        } = await axios.get<StatuspageComponentsResponse>('https://status.onomondo.com/api/v2/components.json');
        const networkStatus = components.find(({ name }) => name === 'Network')?.status;
        const webhooksStatus = components.find(({ name }) => name === 'Webhooks')?.status;
        const apiStatus = components.find(({ name }) => name === 'API')?.status;
        const appStatus = components.find(({ name }) => name === 'App')?.status;
        const hasMajorOutage = [networkStatus, webhooksStatus, apiStatus, appStatus].includes('major_outage');
        const hasPartialOutage = [networkStatus, webhooksStatus, apiStatus, appStatus].includes('partial_outage');
        const hasDegradedPerformance = [networkStatus, webhooksStatus, apiStatus, appStatus].includes(
          'degraded_performance',
        );
        const hasMaintenance = [networkStatus, webhooksStatus, apiStatus, appStatus].includes('under_maintenance');
        const overallStatus: StatuspageStatus = hasMajorOutage
          ? 'major_outage'
          : hasPartialOutage
            ? 'partial_outage'
            : hasDegradedPerformance
              ? 'degraded_performance'
              : hasMaintenance
                ? 'under_maintenance'
                : 'operational';
        setOverallStatus(overallStatus);
        setNetworkStatus(networkStatus);
        setWebhooksStatus(webhooksStatus);
        setApiStatus(apiStatus);
        setAppStatus(appStatus);

        startFetcher(3 * 60 * 1000); // Calls statuspage api every three minutes
      }, waitTime || 0);
    }
  }, [isDevelopment]);

  return {
    overall: overallStatus,
    network: networkStatus,
    webhooks: webhooksStatus,
    api: apiStatus,
    app: appStatus,
  };
}

export default useStatuspage;
